import { Link } from '@tanstack/react-router';
import {
  formatFrequenz,
  MODUL_LABELS,
  MODUL_TEMPLATES,
  type BvModulId,
  type Frequenz,
  type FrequenzPeriod,
} from '../../lib/bv-modul-templates';
import { POSITIONEN_KATALOG } from '../../lib/bv-positions-katalog';
import {
  berechneVorschlagFuerModul,
  kannBerechnetWerden,
  type ObjektPricingInput,
  type PricingShape,
} from '../../lib/bv-auto-pricing';
import {
  divideByInteger,
  isMoneyString,
  multiplyByInteger,
  parseMoneyOr,
} from '../../lib/money-display';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';

export interface Objekt {
  id: string;
  kundeId: string;
  bezeichnung: string;
  anzahlEinheiten: number | null;
  anzahlEingaenge: number;
  aussenflaecheQm: string | null;
  gruenflaecheQm: string | null;
  winterdienstflaecheQm: string | null;
  hatAufzug: boolean;
  flurgroesseKlasse: 'klein' | 'mittel' | 'gross' | null;
  hausGroesseKlasse: 'klein' | 'mittel' | 'gross' | null;
  reinigungProWoche: string | null;
}

export interface FreitextItem {
  text: string;
  frequenz: Frequenz;
}

export interface ModulFormState {
  selectedPositionIds: Set<string>;
  positionFrequenzen: Record<string, Frequenz>;
  freitextItems: FreitextItem[];
  pauschalMonatsbetrag: string;
  einsaetzeProJahr: string;
  einsatzPreis: string;
  bereitschaftProMonat: string;
  saisonMonate: string[];
  initialized: boolean;
}

export const SAISON_MONATE = [
  'Jan',
  'Feb',
  'Mrz',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
] as const;

export function defaultSaisonMonateForModul(modulId: BvModulId): string[] {
  if (modulId === 'winterdienst') return ['Nov', 'Dez', 'Jan', 'Feb', 'Mrz'];
  if (modulId === 'gruenflaechenpflege') return ['Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt'];
  return [];
}

export const FREQUENZ_PERIOD_OPTIONS: { value: FrequenzPeriod; label: string }[] = [
  { value: 'weekly', label: 'pro Woche' },
  { value: 'monthly', label: 'pro Monat' },
  { value: 'quarterly', label: 'pro Quartal' },
  { value: 'yearly', label: 'pro Jahr' },
  { value: 'asNeeded', label: 'nach Bedarf' },
  { value: 'ongoing', label: 'laufend' },
  { value: 'agreed', label: 'nach Vereinbarung' },
];

export const FREITEXT_DEFAULT_FREQUENZ: Frequenz = { count: 1, period: 'monthly' };

const FELD_LABEL: Record<keyof ObjektPricingInput, string> = {
  anzahlEingaenge: 'Anzahl Eingänge',
  hausGroesseKlasse: 'Hausgröße-Klasse',
  anzahlEinheiten: 'Anzahl Einheiten',
  flurgroesseKlasse: 'Flurgröße-Klasse',
  reinigungProWoche: 'Reinigungsfrequenz pro Woche',
  aussenflaecheQm: 'Außenfläche (m²)',
  gruenflaecheQm: 'Grünfläche (m²)',
  winterdienstflaecheQm: 'Winterdienst-Fläche (m²)',
};

export function periodHatCount(period: FrequenzPeriod): boolean {
  return period !== 'asNeeded' && period !== 'ongoing' && period !== 'agreed';
}

export function emptyModulState(): ModulFormState {
  return {
    selectedPositionIds: new Set<string>(),
    positionFrequenzen: {},
    freitextItems: [],
    pauschalMonatsbetrag: '',
    einsaetzeProJahr: '',
    einsatzPreis: '',
    bereitschaftProMonat: '',
    saisonMonate: [],
    initialized: false,
  };
}

export interface ModulPricing {
  monatsbetragNetto: string;
  jahresbetragNetto: string;
  valid: boolean;
}

export function computeModulPricing(modulId: BvModulId, state: ModulFormState): ModulPricing {
  const tpl = MODUL_TEMPLATES[modulId];
  const invalid = { monatsbetragNetto: '0.00', jahresbetragNetto: '0.00', valid: false } as const;
  if (tpl.defaultKalkulationstyp === 'saisonal_verteilt') {
    const einsaetze = positiveInteger(state.einsaetzeProJahr);
    const preis = positiveMoneyOrNull(state.einsatzPreis);
    if (einsaetze == null || preis == null) return invalid;
    const jahr = multiplyByInteger(preis, einsaetze);
    return { monatsbetragNetto: divideByInteger(jahr, 12), jahresbetragNetto: jahr, valid: true };
  }
  if (tpl.defaultKalkulationstyp === 'bereitschaft_plus_einsatz') {
    const bereitschaft = positiveMoneyOrNull(state.bereitschaftProMonat);
    const saisonCount = state.saisonMonate.length;
    if (bereitschaft == null || saisonCount === 0) return invalid;
    const jahr = multiplyByInteger(bereitschaft, saisonCount);
    return { monatsbetragNetto: divideByInteger(jahr, 12), jahresbetragNetto: jahr, valid: true };
  }
  // pauschal (default für die anderen Module)
  const monat = positiveMoneyOrNull(state.pauschalMonatsbetrag);
  if (monat == null) return invalid;
  return {
    monatsbetragNetto: monat,
    jahresbetragNetto: multiplyByInteger(monat, 12),
    valid: true,
  };
}

function positiveMoneyOrNull(s: string): string | null {
  const v = parseMoneyOr(s, '');
  if (v === '' || !isMoneyString(v) || v.startsWith('-') || v === '0.00') return null;
  return v;
}

function positiveInteger(s: string): number | null {
  if (s == null || s === '' || !/^\d+$/.test(s.trim())) return null;
  const n = Number.parseInt(s, 10);
  return n > 0 ? n : null;
}

interface DefaultPositionsResult {
  ids: Set<string>;
  frequenzen: Record<string, Frequenz>;
}

export function defaultPositionsForModul(
  modulId: BvModulId,
  hatAufzug: boolean,
): DefaultPositionsResult {
  const ids = new Set<string>();
  const frequenzen: Record<string, Frequenz> = {};
  for (const p of POSITIONEN_KATALOG[modulId]) {
    if (!p.defaultAktiv) continue;
    if (p.bedingung === 'aufzug' && !hatAufzug) continue;
    ids.add(p.id);
    frequenzen[p.id] = { ...p.frequenz };
  }
  return { ids, frequenzen };
}

export function buildBeschreibungText(
  modulId: BvModulId,
  selected: Set<string>,
  positionFrequenzen: Record<string, Frequenz>,
  freitextItems: FreitextItem[],
): string {
  const tpl = MODUL_TEMPLATES[modulId];
  const intro = tpl.defaultBeschreibungIntro?.trim() ?? '';
  const positionLines = POSITIONEN_KATALOG[modulId]
    .filter((p) => selected.has(p.id))
    .map((p) => {
      const f = positionFrequenzen[p.id] ?? p.frequenz;
      return `- ${p.beschreibung} — ${formatFrequenz(f)}`;
    });
  const customLines = freitextItems
    .map((item) => ({ text: item.text.trim(), frequenz: item.frequenz }))
    .filter((item) => item.text.length > 0)
    .map((item) => `- ${item.text} — ${formatFrequenz(item.frequenz)}`);
  const blocks = [intro, ...positionLines, ...customLines].filter((s) => s.length > 0);
  return blocks.join('\n');
}

interface ModulDetailCardCommonProps {
  modulId: BvModulId;
  state: ModulFormState;
  onTogglePosition: (positionId: string) => void;
  onSetPositionFrequenz: (positionId: string, patch: Partial<Frequenz>) => void;
  onAddFreitext: () => void;
  onUpdateFreitextText: (idx: number, v: string) => void;
  onSetFreitextFrequenz: (idx: number, patch: Partial<Frequenz>) => void;
  onRemoveFreitext: (idx: number) => void;
  onChangePauschal: (v: string) => void;
  onChangeEinsaetzeProJahr?: (v: string) => void;
  onChangeEinsatzPreis?: (v: string) => void;
  onChangeBereitschaft?: (v: string) => void;
  onChangeSaisonMonate?: (months: string[]) => void;
}

interface ModulDetailCardWithPricing extends ModulDetailCardCommonProps {
  showAutoPricing?: true;
  objekt: Objekt;
  objektPricingInput: Partial<ObjektPricingInput>;
  pricing: PricingShape;
  onApplyAutoPricing: () => void;
}

interface ModulDetailCardWithoutPricing extends ModulDetailCardCommonProps {
  showAutoPricing: false;
}

export type ModulDetailCardProps = ModulDetailCardWithPricing | ModulDetailCardWithoutPricing;

export function ModulDetailCard(props: ModulDetailCardProps) {
  const { modulId, state } = props;
  const tpl = MODUL_TEMPLATES[modulId];
  const positions = POSITIONEN_KATALOG[modulId];
  const withAutoPricing = props.showAutoPricing !== false;

  return (
    <section
      data-testid={`modul-detail-${modulId}`}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-sm font-semibold text-slate-800">{MODUL_LABELS[modulId]}</h2>
      <p className="mt-1 text-xs text-slate-600">{tpl.defaultBeschreibungIntro}</p>

      <fieldset className="mt-3">
        <legend className="text-xs font-medium text-slate-700">Katalog-Positionen</legend>
        <div className="mt-2 space-y-1">
          {positions.map((p) => {
            const checked = state.selectedPositionIds.has(p.id);
            const frequenz = state.positionFrequenzen[p.id] ?? p.frequenz;
            return (
              <div
                key={p.id}
                className="flex items-start gap-2 text-xs text-slate-700"
                data-testid={`position-${modulId}-${p.id}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={() => props.onTogglePosition(p.id)}
                  aria-label={p.beschreibung}
                />
                <div className="flex-1">
                  <span>{p.beschreibung}</span>
                  {checked ? (
                    <FrequenzEditor
                      testIdPrefix={`position-frequenz-${modulId}-${p.id}`}
                      ariaLabel={`Turnus für ${p.beschreibung}`}
                      frequenz={frequenz}
                      onChange={(patch) => props.onSetPositionFrequenz(p.id, patch)}
                    />
                  ) : (
                    <span className="ml-1 text-slate-500">— {formatFrequenz(frequenz)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-slate-700">Freitext-Positionen</legend>
        <div className="mt-2 space-y-2">
          {state.freitextItems.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <Input
                value={item.text}
                onChange={(e) => props.onUpdateFreitextText(idx, e.target.value)}
                placeholder="z.B. Sondereinsatz Treppenhaus-Grundreinigung jährlich"
                data-testid={`freitext-${modulId}-${idx}`}
              />
              <FrequenzEditor
                testIdPrefix={`freitext-frequenz-${modulId}-${idx}`}
                ariaLabel={`Turnus für Freitext ${idx + 1}`}
                frequenz={item.frequenz}
                onChange={(patch) => props.onSetFreitextFrequenz(idx, patch)}
              />
              <Button
                variant="ghost"
                onClick={() => props.onRemoveFreitext(idx)}
                aria-label={`Freitext ${idx + 1} entfernen`}
              >
                ×
              </Button>
            </div>
          ))}
          <Button variant="ghost" onClick={props.onAddFreitext}>
            + Position hinzufügen
          </Button>
        </div>
      </fieldset>

      <div className={`mt-4 grid gap-4 ${withAutoPricing ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {tpl.defaultKalkulationstyp === 'saisonal_verteilt' ? (
          <SaisonalVerteiltFelder
            modulId={modulId}
            state={state}
            onChangeEinsaetzeProJahr={(v) => props.onChangeEinsaetzeProJahr?.(v)}
            onChangeEinsatzPreis={(v) => props.onChangeEinsatzPreis?.(v)}
          />
        ) : tpl.defaultKalkulationstyp === 'bereitschaft_plus_einsatz' ? (
          <BereitschaftPlusEinsatzFelder
            modulId={modulId}
            state={state}
            onChangeBereitschaft={(v) => props.onChangeBereitschaft?.(v)}
            onChangeSaisonMonate={(arr) => props.onChangeSaisonMonate?.(arr)}
          />
        ) : (
          <div>
            <label
              className="block text-xs font-medium text-slate-700"
              htmlFor={`pauschal-${modulId}`}
            >
              Pauschalbetrag €/Monat *
            </label>
            <Input
              id={`pauschal-${modulId}`}
              type="number"
              step="0.01"
              min="0"
              value={state.pauschalMonatsbetrag}
              onChange={(e) => props.onChangePauschal(e.target.value)}
              data-testid={`pauschal-${modulId}`}
            />
          </div>
        )}
        {props.showAutoPricing !== false ? (
          <AutoPricingBlock
            modulId={modulId}
            state={state}
            objekt={props.objekt}
            objektPricingInput={props.objektPricingInput}
            pricing={props.pricing}
            onApply={props.onApplyAutoPricing}
          />
        ) : null}
      </div>
    </section>
  );
}

function SaisonalVerteiltFelder(props: {
  modulId: BvModulId;
  state: ModulFormState;
  onChangeEinsaetzeProJahr: (v: string) => void;
  onChangeEinsatzPreis: (v: string) => void;
}) {
  const { modulId, state } = props;
  const pricing = computeModulPricing(modulId, state);
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label
          className="block text-xs font-medium text-slate-700"
          htmlFor={`einsaetze-${modulId}`}
        >
          Einsätze pro Jahr *
        </label>
        <Input
          id={`einsaetze-${modulId}`}
          type="number"
          step="1"
          min="1"
          value={state.einsaetzeProJahr}
          onChange={(e) => props.onChangeEinsaetzeProJahr(e.target.value)}
          data-testid={`einsaetze-${modulId}`}
        />
      </div>
      <div>
        <label
          className="block text-xs font-medium text-slate-700"
          htmlFor={`einsatzpreis-${modulId}`}
        >
          Preis je Einsatz €*
        </label>
        <Input
          id={`einsatzpreis-${modulId}`}
          type="number"
          step="0.01"
          min="0"
          value={state.einsatzPreis}
          onChange={(e) => props.onChangeEinsatzPreis(e.target.value)}
          data-testid={`einsatzpreis-${modulId}`}
        />
      </div>
      <p className="col-span-2 text-xs text-slate-600" data-testid={`vorschau-${modulId}`}>
        {pricing.valid
          ? `${formatEur(pricing.jahresbetragNetto)}/Jahr → ${formatEur(pricing.monatsbetragNetto)}/Monat (gleichmäßig auf 12 Monate verteilt)`
          : 'Trage Einsätze pro Jahr und Preis je Einsatz ein.'}
      </p>
    </div>
  );
}

function BereitschaftPlusEinsatzFelder(props: {
  modulId: BvModulId;
  state: ModulFormState;
  onChangeBereitschaft: (v: string) => void;
  onChangeSaisonMonate: (months: string[]) => void;
}) {
  const { modulId, state } = props;
  const pricing = computeModulPricing(modulId, state);
  const saisonSet = new Set(state.saisonMonate);
  function toggleMonat(m: string): void {
    const next = saisonSet.has(m)
      ? state.saisonMonate.filter((x) => x !== m)
      : [...state.saisonMonate, m];
    props.onChangeSaisonMonate(next);
  }
  return (
    <div className="grid grid-cols-1 gap-3">
      <div>
        <label
          className="block text-xs font-medium text-slate-700"
          htmlFor={`bereitschaft-${modulId}`}
        >
          Bereitschaft €/Saison-Monat *
        </label>
        <Input
          id={`bereitschaft-${modulId}`}
          type="number"
          step="0.01"
          min="0"
          value={state.bereitschaftProMonat}
          onChange={(e) => props.onChangeBereitschaft(e.target.value)}
          data-testid={`bereitschaft-${modulId}`}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-700">Saison-Monate *</label>
        <div className="mt-1 flex flex-wrap gap-1" data-testid={`saisonmonate-${modulId}`}>
          {SAISON_MONATE.map((m) => {
            const aktiv = saisonSet.has(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMonat(m)}
                aria-pressed={aktiv}
                data-testid={`saisonmonat-${modulId}-${m}`}
                className={
                  aktiv
                    ? 'rounded-md border border-brand-300 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-800'
                    : 'rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50'
                }
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-slate-600" data-testid={`vorschau-${modulId}`}>
        {pricing.valid
          ? `${formatEur(state.bereitschaftProMonat || '0')}/Saison-Monat × ${state.saisonMonate.length} Monate = ${formatEur(pricing.jahresbetragNetto)}/Jahr → ${formatEur(pricing.monatsbetragNetto)}/Monat (gleichmäßig auf 12 Monate verteilt)`
          : 'Trage Bereitschaftsbetrag ein und wähle Saison-Monate aus.'}
      </p>
    </div>
  );
}

function formatEur(decimalString: string): string {
  return `${decimalString.replace('.', ',')} €`;
}

function AutoPricingBlock(props: {
  modulId: BvModulId;
  state: ModulFormState;
  objekt: Objekt;
  objektPricingInput: Partial<ObjektPricingInput>;
  pricing: PricingShape;
  onApply: () => void;
}) {
  const { modulId } = props;
  const tpl = MODUL_TEMPLATES[modulId];
  const isSaisonalVerteilt = tpl.defaultKalkulationstyp === 'saisonal_verteilt';
  const isBereitschaft = tpl.defaultKalkulationstyp === 'bereitschaft_plus_einsatz';

  const stammdatenOk = kannBerechnetWerden(modulId, props.objektPricingInput);
  const vorschlag = stammdatenOk
    ? berechneVorschlagFuerModul(modulId, props.objektPricingInput, props.pricing)
    : null;
  const fehlend = stammdatenOk
    ? []
    : berechneVorschlagFuerModul(modulId, props.objektPricingInput, props.pricing).fehlendeFelder;

  // Saisonal-verteilt braucht zusätzlich einsaetzeProJahr aus dem Form-State,
  // um den Vorschlag in einen Preis pro Einsatz umzurechnen.
  const einsaetze = Number(props.state.einsaetzeProJahr);
  const einsaetzeOk = Number.isFinite(einsaetze) && einsaetze > 0 && Number.isInteger(einsaetze);
  // Bereitschaft+Einsatz braucht saisonMonate, um den Vorschlag in einen
  // Bereitschaftsbetrag pro Saison-Monat umzurechnen.
  const saisonCount = props.state.saisonMonate.length;
  const saisonOk = saisonCount > 0;
  const canCompute =
    stammdatenOk && (!isSaisonalVerteilt || einsaetzeOk) && (!isBereitschaft || saisonOk);

  let buttonLabel: string;
  if (!canCompute) {
    buttonLabel = 'Auto-Berechnen (deaktiviert)';
  } else if (isSaisonalVerteilt && vorschlag) {
    const preisProEinsatz = (vorschlag.vorschlagBetrag * 12) / einsaetze;
    buttonLabel = `Übernehmen: ${preisProEinsatz.toFixed(2)} €/Einsatz`;
  } else if (isBereitschaft && vorschlag) {
    const bereitschaft = (vorschlag.vorschlagBetrag * 12) / saisonCount;
    buttonLabel = `Übernehmen: ${bereitschaft.toFixed(2)} €/Saison-Monat`;
  } else if (vorschlag) {
    buttonLabel = `Übernehmen: ${vorschlag.vorschlagBetrag.toFixed(2)} €`;
  } else {
    buttonLabel = 'Auto-Berechnen (deaktiviert)';
  }

  return (
    <div>
      <label className="block text-xs font-medium text-slate-700">Auto-Vorschlag</label>
      <Button
        variant="ghost"
        disabled={!canCompute}
        onClick={props.onApply}
        data-testid={`auto-${modulId}`}
        title={vorschlag?.herleitung}
      >
        {buttonLabel}
      </Button>
      {!stammdatenOk ? (
        <p className="mt-1 text-xs text-slate-500" data-testid={`fehlend-${modulId}`}>
          Fehlende Felder am Objekt:{' '}
          {fehlend.map((f) => FELD_LABEL[f as keyof ObjektPricingInput] ?? f).join(', ')}.{' '}
          <Link
            to="/objekte/$id"
            params={{ id: props.objekt.id }}
            className="text-brand-600 underline"
          >
            Objekt bearbeiten
          </Link>
        </p>
      ) : isSaisonalVerteilt && !einsaetzeOk ? (
        <p className="mt-1 text-xs text-slate-500" data-testid={`hint-einsaetze-${modulId}`}>
          Bitte zuerst Einsätze pro Jahr eintragen.
        </p>
      ) : isBereitschaft && !saisonOk ? (
        <p className="mt-1 text-xs text-slate-500" data-testid={`hint-saison-${modulId}`}>
          Bitte mindestens einen Saison-Monat auswählen.
        </p>
      ) : vorschlag ? (
        <p className="mt-1 text-xs text-slate-500">{vorschlag.herleitung}</p>
      ) : null}
    </div>
  );
}

export function FrequenzEditor(props: {
  testIdPrefix: string;
  ariaLabel: string;
  frequenz: Frequenz;
  onChange: (patch: Partial<Frequenz>) => void;
}) {
  const showCount = periodHatCount(props.frequenz.period);
  return (
    <span className="ml-2 inline-flex items-center gap-1 align-middle">
      <input
        type="number"
        min="1"
        max="99"
        step="1"
        value={showCount ? props.frequenz.count : ''}
        disabled={!showCount}
        onChange={(e) => {
          const n = Number.parseInt(e.target.value, 10);
          if (Number.isFinite(n) && n > 0) props.onChange({ count: n });
        }}
        aria-label={`Anzahl ${props.ariaLabel}`}
        data-testid={`${props.testIdPrefix}-count`}
        className="w-14 rounded border border-slate-300 bg-white px-1 py-0.5 text-xs disabled:bg-slate-100 disabled:text-slate-400"
      />
      <Select
        value={props.frequenz.period}
        onChange={(e) => props.onChange({ period: e.target.value as FrequenzPeriod })}
        aria-label={`Turnus ${props.ariaLabel}`}
        data-testid={`${props.testIdPrefix}-period`}
        className="!w-auto !py-0.5 text-xs"
      >
        {FREQUENZ_PERIOD_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    </span>
  );
}
